import { useState } from 'react';
import { calcOptionPoolPlan } from '@/utils/equityMath';
import type { PoolPlanRound, PoolAllocationBand, PoolProjectionRow } from '@/utils/equityMath';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Plus, Trash2, Play, Users, AlertCircle, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface PoolStatus {
  /** Fully diluted shares on issue, the pool included. */
  fullyDilutedShares: number;
  authorizedShares: number;
  grantedShares: number;
}

type FundingRound = PoolPlanRound;

interface AllocationPlan extends PoolAllocationBand {
  id: string;
}

interface PoolProjection extends PoolProjectionRow {
  recommendation: string;
}

export default function OptionPoolPlanner() {
  const [currentPool, setCurrentPool] = useState<PoolStatus>({
    fullyDilutedShares: 100000000,
    authorizedShares: 10000000,
    grantedShares: 3000000,
  });

  const [fundingRounds, setFundingRounds] = useState<FundingRound[]>([
    {
      id: '1',
      name: 'Series A',
      preMoneyValuation: 10000000,
      newMoneyRaised: 5000000,
      poolRefreshPct: 15,
      plannedDate: '2025-06-01',
      keyHires: 8,
    },
    {
      id: '2',
      name: 'Series B',
      preMoneyValuation: 25000000,
      newMoneyRaised: 15000000,
      poolRefreshPct: 12,
      plannedDate: '2026-06-01',
      keyHires: 12,
    },
  ]);

  const [allocations, setAllocations] = useState<AllocationPlan[]>([
    {
      id: '1',
      role: 'Engineering',
      level: 'Senior',
      count: 5,
      sharesPerPerson: 50000,
    },
    {
      id: '2',
      role: 'Engineering',
      level: 'Mid-Level',
      count: 8,
      sharesPerPerson: 25000,
    },
    {
      id: '3',
      role: 'Product',
      level: 'Senior',
      count: 3,
      sharesPerPerson: 40000,
    },
    {
      id: '4',
      role: 'Sales',
      level: 'Senior',
      count: 4,
      sharesPerPerson: 35000,
    },
    {
      id: '5',
      role: 'Operations',
      level: 'Mid-Level',
      count: 3,
      sharesPerPerson: 20000,
    },
  ]);

  const [projections, setProjections] = useState<PoolProjection[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [planWarnings, setPlanWarnings] = useState<string[]>([]);

  const updateCurrentPool = (field: keyof PoolStatus, value: number) => {
    setCurrentPool({ ...currentPool, [field]: value });
  };

  const addRound = () => {
    const newId = String(Date.now());
    setFundingRounds([
      ...fundingRounds,
      {
        id: newId,
        name: `Series ${String.fromCharCode(65 + fundingRounds.length)}`,
        preMoneyValuation: 0,
        newMoneyRaised: 0,
        poolRefreshPct: 15,
        plannedDate: '',
        keyHires: 0,
      },
    ]);
  };

  const updateRound = (id: string, field: keyof FundingRound, value: string | number) => {
    setFundingRounds(fundingRounds.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const removeRound = (id: string) => {
    setFundingRounds(fundingRounds.filter((r) => r.id !== id));
  };

  const addAllocation = () => {
    const newId = String(Date.now());
    setAllocations([
      ...allocations,
      {
        id: newId,
        role: '',
        level: 'Mid-Level',
        count: 1,
        sharesPerPerson: 10000,
      },
    ]);
  };

  const updateAllocation = (id: string, field: keyof AllocationPlan, value: string | number) => {
    setAllocations(allocations.map((a) => (a.id === id ? { ...a, [field]: value } : a)));
  };

  /** Shares a band consumes: headcount times the grant each person receives. */
  const bandTotal = (a: AllocationPlan) => Math.max(0, a.count) * Math.max(0, a.sharesPerPerson);

  const removeAllocation = (id: string) => {
    setAllocations(allocations.filter((a) => a.id !== id));
  };

  const recommendationFor = (percentAvailable: number) => {
    if (percentAvailable < 20) return 'Critical: Pool nearly depleted';
    if (percentAvailable < 40) return 'Warning: Consider larger refresh';
    if (percentAvailable > 70) return 'Optimal: Healthy buffer';
    return 'Good: Adequate runway';
  };

  const runAnalysis = () => {
    setAnalyzing(true);
    setTimeout(() => {
      const plan = calcOptionPoolPlan(
        {
          fullyDilutedShares: currentPool.fullyDilutedShares,
          poolAuthorisedShares: currentPool.authorizedShares,
          poolGrantedShares: currentPool.grantedShares,
        },
        fundingRounds,
        allocations
      );

      setPlanWarnings(plan.warnings);
      setProjections(
        plan.rows.map((row) => ({
          ...row,
          date: row.round === 'Current' ? new Date().toISOString().split('T')[0] : row.date,
          recommendation: recommendationFor(row.percentAvailable),
        }))
      );
      setAnalyzing(false);
    }, 800);
  };

  // Chart data
  const poolDepletionData = projections.map((p) => ({
    round: p.round,
    Available: p.availableShares,
    Granted: p.grantedShares,
    'Total Pool': p.poolShares,
  }));

  const allocationByRole = Object.values(
    allocations.reduce(
      (acc, alloc) => {
        if (!acc[alloc.role]) {
          acc[alloc.role] = { role: alloc.role, shares: 0, count: 0 };
        }
        acc[alloc.role].shares += bandTotal(alloc);
        acc[alloc.role].count += alloc.count;
        return acc;
      },
      {} as Record<string, { role: string; shares: number; count: number }>
    )
  );

  const dilutionTrendData = projections.map((p) => ({
    round: p.round,
    dilution: p.poolPctOfCompany,
    available: p.percentAvailable,
  }));

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))',
  ];

  const totalPlannedAllocation = allocations.reduce((sum, a) => sum + bandTotal(a), 0);
  const availableToday = Math.max(0, currentPool.authorizedShares - currentPool.grantedShares);
  const poolUtilization = availableToday > 0 ? (totalPlannedAllocation / availableToday) * 100 : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Option Pool Planning Tool
          </CardTitle>
          <CardDescription>
            Determine optimal pool sizes, refresh timing, and allocation strategies across funding
            rounds
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {planWarnings.length > 0 && (
            <div className="space-y-2">
              {planWarnings.map((warning, i) => (
                <div
                  key={i}
                  className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
                >
                  <p className="text-sm text-muted-foreground">{warning}</p>
                </div>
              ))}
            </div>
          )}

          {/* Current Pool Status */}
          <div>
            <h3 className="font-semibold mb-1">Current Pool Status</h3>
            <p className="text-xs text-muted-foreground mb-3">
              The pool is only meaningful against the shares it dilutes, so the fully diluted count
              is an input here rather than an assumption made for you.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Fully Diluted Shares</Label>
                <Input
                  type="number"
                  min="0"
                  value={currentPool.fullyDilutedShares}
                  onChange={(e) =>
                    updateCurrentPool('fullyDilutedShares', Math.max(0, Number(e.target.value)))
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Authorized Shares</Label>
                <Input
                  type="number"
                  value={currentPool.authorizedShares}
                  onChange={(e) => updateCurrentPool('authorizedShares', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Granted Shares</Label>
                <Input
                  type="number"
                  value={currentPool.grantedShares}
                  onChange={(e) => updateCurrentPool('grantedShares', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Available Shares</Label>
                <div className="mt-1 px-3 py-2 bg-muted rounded-md font-medium">
                  {(currentPool.authorizedShares - currentPool.grantedShares).toLocaleString()}
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">% Available</Label>
                <div className="mt-1 px-3 py-2 bg-muted rounded-md font-medium">
                  {currentPool.authorizedShares > 0
                    ? Math.round(
                        ((currentPool.authorizedShares - currentPool.grantedShares) /
                          currentPool.authorizedShares) *
                          100
                      )
                    : 0}
                  %
                </div>
              </div>
            </div>
          </div>

          {/* Funding Rounds */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Funding Rounds & Pool Refresh</h3>
              <Button onClick={addRound} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" /> Add Round
              </Button>
            </div>
            <div className="space-y-3">
              {fundingRounds.map((round) => (
                <div
                  key={round.id}
                  className="grid grid-cols-12 gap-2 items-end p-3 border rounded-lg"
                >
                  <div className="col-span-2">
                    <Label className="text-xs">Round Name</Label>
                    <Input
                      value={round.name}
                      onChange={(e) => updateRound(round.id, 'name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Pre-Money ($)</Label>
                    <Input
                      type="number"
                      value={round.preMoneyValuation}
                      onChange={(e) =>
                        updateRound(round.id, 'preMoneyValuation', Number(e.target.value))
                      }
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">New Money ($)</Label>
                    <Input
                      type="number"
                      value={round.newMoneyRaised}
                      onChange={(e) =>
                        updateRound(round.id, 'newMoneyRaised', Number(e.target.value))
                      }
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Pool Refresh %</Label>
                    <Input
                      type="number"
                      value={round.poolRefreshPct}
                      onChange={(e) =>
                        updateRound(round.id, 'poolRefreshPct', Number(e.target.value))
                      }
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Key Hires</Label>
                    <Input
                      type="number"
                      value={round.keyHires}
                      onChange={(e) => updateRound(round.id, 'keyHires', Number(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-xs">Date</Label>
                    <Input
                      type="date"
                      value={round.plannedDate}
                      onChange={(e) => updateRound(round.id, 'plannedDate', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button onClick={() => removeRound(round.id)} size="sm" variant="ghost">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Allocation Planning */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Allocation Planning</h3>
              <Button onClick={addAllocation} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" /> Add Role
              </Button>
            </div>
            <div className="space-y-2">
              {allocations.map((alloc) => (
                <div key={alloc.id} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-3">
                    <Input
                      placeholder="Role"
                      value={alloc.role}
                      onChange={(e) => updateAllocation(alloc.id, 'role', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <select
                      value={alloc.level}
                      onChange={(e) => updateAllocation(alloc.id, 'level', e.target.value)}
                      className="w-full px-3 py-2 border rounded-md text-sm"
                    >
                      <option value="Executive">Executive</option>
                      <option value="Senior">Senior</option>
                      <option value="Mid-Level">Mid-Level</option>
                      <option value="Junior">Junior</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      placeholder="Count"
                      value={alloc.count}
                      onChange={(e) => updateAllocation(alloc.id, 'count', Number(e.target.value))}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      placeholder="Shares/Person"
                      value={alloc.sharesPerPerson}
                      onChange={(e) =>
                        updateAllocation(alloc.id, 'sharesPerPerson', Number(e.target.value))
                      }
                    />
                  </div>
                  <div className="col-span-2">
                    <div className="px-3 py-2 bg-muted rounded-md text-sm font-medium">
                      {bandTotal(alloc).toLocaleString()}
                    </div>
                  </div>
                  <div className="col-span-1">
                    <Button onClick={() => removeAllocation(alloc.id)} size="sm" variant="ghost">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Total Planned Allocation:</span>
                <span className="text-lg font-bold">
                  {totalPlannedAllocation.toLocaleString()} shares
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm text-muted-foreground">Pool Utilization:</span>
                <Badge
                  variant={
                    poolUtilization > 100
                      ? 'destructive'
                      : poolUtilization > 70
                        ? 'default'
                        : 'secondary'
                  }
                >
                  {poolUtilization.toFixed(1)}%
                </Badge>
              </div>
            </div>
          </div>

          <Button onClick={runAnalysis} disabled={analyzing} className="w-full">
            <Play className="h-4 w-4 mr-2" />
            {analyzing ? 'Analyzing...' : 'Run Pool Analysis'}
          </Button>
        </CardContent>
      </Card>

      {projections.length > 0 && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Pool Projection Analysis</CardTitle>
              <CardDescription>
                Multi-round option pool planning with strategic recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="timeline">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="timeline">Pool Timeline</TabsTrigger>
                  <TabsTrigger value="allocation">Allocation Mix</TabsTrigger>
                  <TabsTrigger value="dilution">Dilution Impact</TabsTrigger>
                  <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
                </TabsList>

                <TabsContent value="timeline" className="space-y-4">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={poolDepletionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="round" />
                      <YAxis label={{ value: 'Shares', angle: -90, position: 'insideLeft' }} />
                      <Tooltip formatter={(value: number) => value.toLocaleString()} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="Total Pool"
                        stroke="hsl(var(--chart-1))"
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="Granted"
                        stroke="hsl(var(--chart-5))"
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="Available"
                        stroke="hsl(var(--chart-2))"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </TabsContent>

                <TabsContent value="allocation" className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={allocationByRole}
                          dataKey="shares"
                          nameKey="role"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={(entry) =>
                            `${entry.role}: ${((entry.shares / totalPlannedAllocation) * 100).toFixed(1)}%`
                          }
                        >
                          {allocationByRole.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => value.toLocaleString()} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div>
                      <h4 className="font-semibold mb-3">Allocation by Role</h4>
                      <div className="space-y-2">
                        {allocationByRole.map((role, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center p-2 border rounded"
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                              />
                              <span className="font-medium">{role.role}</span>
                              <Badge variant="outline">{role.count} people</Badge>
                            </div>
                            <span className="font-semibold">{role.shares.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="dilution" className="space-y-4">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={dilutionTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="round" />
                      <YAxis label={{ value: 'Percentage', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="dilution" fill="hsl(var(--chart-3))" name="Pool Dilution %" />
                      <Bar dataKey="available" fill="hsl(var(--chart-2))" name="% Pool Available" />
                    </BarChart>
                  </ResponsiveContainer>
                </TabsContent>

                <TabsContent value="recommendations" className="space-y-4">
                  <div className="rounded-lg border">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Round</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">Pool Size</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">Available</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">
                            % Available
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {projections.map((proj, idx) => (
                          <tr key={idx} className="hover:bg-muted/30">
                            <td className="px-4 py-3 font-medium">{proj.round}</td>
                            <td className="px-4 py-3 text-sm">{proj.date}</td>
                            <td className="px-4 py-3 text-right">
                              {Math.round(proj.poolShares).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {Math.round(proj.availableShares).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold">
                              {proj.percentAvailable.toFixed(1)}%
                            </td>
                            <td className="px-4 py-3">
                              <Badge
                                variant={
                                  proj.recommendation.includes('Critical')
                                    ? 'destructive'
                                    : proj.recommendation.includes('Warning')
                                      ? 'default'
                                      : 'secondary'
                                }
                              >
                                {proj.recommendation}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="space-y-3">
                    <Alert>
                      <CheckCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Best Practices:</strong>
                        <ul className="mt-2 space-y-1 text-sm ml-4 list-disc">
                          <li>Maintain at least 30-40% of pool available at all times</li>
                          <li>Plan pool refresh before critical hiring phases</li>
                          <li>Target 10-15% post-money pool size for early rounds</li>
                          <li>Reserve higher grants (0.5-2%) for executive hires</li>
                          <li>Consider market benchmarks when setting grant sizes</li>
                        </ul>
                      </AlertDescription>
                    </Alert>

                    {projections.some((p) => p.percentAvailable < 30) && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          <strong>Warning:</strong> Some rounds show pool depletion risk. Consider
                          increasing pool refresh percentages or adjusting allocation plans.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
